import dayjs from "dayjs";
import { useAtom } from "jotai";
import { ScheduleFormSchema } from "components/ScheduleVisitForm/form";
import { ScheduleVisitTileData } from "components/ScheduleVisitForm/types";
import {
  DoctorDraftVisitsDocument,
  DoctorGetPatientDocument,
  DoctorGetVisitDataForEditFormQuery,
  DoctorMSPShiftVisitType,
  DoctorMSShiftsSlot,
  DoctorPatientProfileVisitDocument,
  DoctorUpcomingVisitsDocument,
  DoctorVisitDocument,
  EdmGetAllVisitsForDeletedShiftsDocument,
  EdmSearchAvailabilityDocument,
  EdmSearchAvailabilityForDayDocument,
  useDoctorCreateVisitSlotMutation,
  useDoctorForceCreateVisitMutation,
  useDoctorForceEditVisitMutation,
  useDoctorUpdateVisitSlotMutation,
} from "lib/graphql/megaSchema";
import { workspaceTilesAtom } from "lib/tools/createWorkspace/atoms";
import { isDataTile } from "lib/tools/createWorkspace/tools";
import {
  getVisibleTiles,
  useTileSystem,
} from "lib/tools/createWorkspace/useTileSystem";
import { toaster } from "lib/tools/toaster";

type Props = {
  scheduleVisitTileData: ScheduleVisitTileData;
  visitData: DoctorGetVisitDataForEditFormQuery | undefined;
};

export const useScheduleVisitMutations = ({
  scheduleVisitTileData,
  visitData,
}: Props) => {
  const { mode, patientId } = scheduleVisitTileData;
  const [tiles] = useAtom(workspaceTilesAtom);
  const { updateTileData } = useTileSystem();

  const [createVisitSlotMutation, { loading: createVisitLoading }] =
    useDoctorCreateVisitSlotMutation({
      refetchQueries: [
        DoctorPatientProfileVisitDocument,
        DoctorGetPatientDocument,
        DoctorDraftVisitsDocument,
      ],
    });

  const [forceCreateVisitMutation, { loading: forceCreateVisitLoading }] =
    useDoctorForceCreateVisitMutation();

  const [forceEditVisitMutation, { loading: forceEditVisitLoading }] =
    useDoctorForceEditVisitMutation();

  const [updateVisitSlotMutation, { loading: updateVisitLoading }] =
    useDoctorUpdateVisitSlotMutation({
      refetchQueries: [
        DoctorPatientProfileVisitDocument,
        DoctorGetPatientDocument,
        DoctorDraftVisitsDocument,
        DoctorVisitDocument,
      ],
    });

  const updateVisitsWithDeletedShiftTileData = (visitId: string) => {
    const visibleTiles = getVisibleTiles(tiles);

    const visitsWithDeletedShiftTileId = visibleTiles.find(
      (tile) =>
        isDataTile(tile) && tile.name === "visits-with-deleted-shift-table",
    )?.id;

    if (!visitsWithDeletedShiftTileId) {
      return;
    }

    updateTileData({
      tileId: visitsWithDeletedShiftTileId,
      handler: ({ currentTile }) => {
        if (currentTile.name !== "visits-with-deleted-shift-table") {
          return currentTile.data;
        }

        const filteredVisits = currentTile.data.visits.filter(
          (visit) => visit.referenceOldVisitId !== visitId,
        );

        return { ...currentTile.data, visits: filteredVisits };
      },
    });
  };

  const createVisit = async (variables: {
    serviceCode?: string;
    shiftVisitType: DoctorMSPShiftVisitType;
    shiftsSlot: DoctorMSShiftsSlot[];
    patientId: string;
  }) => {
    const createVisitPromise = createVisitSlotMutation({
      variables,
      refetchQueries: [
        DoctorUpcomingVisitsDocument,
        EdmSearchAvailabilityDocument,
        EdmSearchAvailabilityForDayDocument,
      ],
      awaitRefetchQueries: true,
    });

    await toaster.promise(createVisitPromise, {
      success: "Poprawnie utworzono wizytę!",
      loading: "Tworzę wizytę",
      error: "Nie udało się utworzyć wizyty",
    });
  };

  const forceCreateVisit = async (
    formData: ScheduleFormSchema,
    patientId: string,
  ) => {
    const {
      plannedDate,
      plannedTime,
      staffId: doctorId,
      visitType,
      duration,
      organizationId,
      officeId,
    } = formData;

    if (!doctorId || !plannedDate || !plannedTime || !duration) {
      return;
    }

    const forceCreateVisitPromise = forceCreateVisitMutation({
      variables: {
        patientId,
        input: {
          doctorId,
          plannedStart: dayjs(`${plannedDate} ${plannedTime}`).toISOString(),
          duration,
          visitType,
          organizationId: officeId ?? organizationId ?? "P",
        },
      },
      refetchQueries: [DoctorUpcomingVisitsDocument],
      awaitRefetchQueries: true,
    });

    await toaster.promise(forceCreateVisitPromise, {
      success: "Poprawnie wymuszono wizytę!",
      loading: "Wymuszam wizytę",
      error: "Nie udało się wymusić wizyty",
    });
  };

  const editVisit = async (variables: {
    id: string;
    visitType: DoctorMSPShiftVisitType;
    shiftsSlot: DoctorMSShiftsSlot[];
  }) => {
    const updateVisitPromise = updateVisitSlotMutation({
      variables,
      refetchQueries: [
        DoctorUpcomingVisitsDocument,
        EdmSearchAvailabilityDocument,
        EdmSearchAvailabilityForDayDocument,
        EdmGetAllVisitsForDeletedShiftsDocument,
      ],
      awaitRefetchQueries: true,
    });

    await toaster.promise(updateVisitPromise, {
      success: () => {
        updateVisitsWithDeletedShiftTileData(variables.id);

        return "Poprawnie edytowano wizytę!";
      },
      loading: "Edytuję wizytę",
      error: (err) => {
        if (err.message === "Cannot modify visit two hours before it.") {
          return "Nie można edytować wizyty dwie godziny przed planowanym rozpoczęciem";
        }

        return "Nie udało się edytować wizyty";
      },
    });
  };

  const forceEditVisit = async (
    formData: ScheduleFormSchema,
    visitId: string | undefined,
  ) => {
    const {
      plannedDate,
      plannedTime,
      staffId: doctorId,
      duration,
      visitType,
      organizationId,
      officeId,
    } = formData;

    if (!doctorId || !plannedDate || !plannedTime || !duration || !visitId) {
      return;
    }

    const forceEditVisitPromise = forceEditVisitMutation({
      variables: {
        id: visitId,
        input: {
          doctorId,
          plannedStart: dayjs(`${plannedDate} ${plannedTime}`).toISOString(),
          duration,
          visitType,
          organizationId: officeId ?? organizationId ?? "P",
        },
      },
      refetchQueries: [
        DoctorUpcomingVisitsDocument,
        DoctorVisitDocument,
        EdmGetAllVisitsForDeletedShiftsDocument,
      ],
      awaitRefetchQueries: true,
    });

    await toaster.promise(forceEditVisitPromise, {
      success: () => {
        updateVisitsWithDeletedShiftTileData(visitId);

        return "Poprawnie wymuszono edycję wizyty!";
      },
      error: "Nie udało się wymusić edycji wizyty",
      loading: "Wymuszam edycję wizyty",
    });
  };

  const handleForceVisit = async (formData: ScheduleFormSchema) => {
    if (mode === "forceCreate") {
      await forceCreateVisit(formData, patientId);
    }

    await forceEditVisit(formData, visitData?.doctorVisit.id);
  };

  const loading =
    forceCreateVisitLoading ||
    forceEditVisitLoading ||
    createVisitLoading ||
    updateVisitLoading;

  return {
    handleForceVisit,
    createVisit,
    editVisit,
    loading,
  };
};
